import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { detectUppercaseVariables, updateVariableValue } from "@utils";
import { useWorkflowGeneration } from "../../../../context/WorkflowGenerationContext";

const getDisplayValue = (value, valueType) => {
    if (!value) return "";
    if (valueType === "template") return value.replace(/^`|`$/g, "");
    if (valueType === "double_quotes" || valueType === "single_quotes") return value.replace(/^["']|["']$/g, "");
    if (valueType === "object" || valueType === "array") {
        try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
    }
    return value.toString();
};

const emptyMessage = (
    <div className="p-4 text-sm text-gray-500 text-center">
        No configurable variables detected. Variables must be UPPERCASE (e.g., SYSTEM_PROMPT, API_URL).
    </div>
);

export default function ParametersTab({ step }) {
    const { getStepConfig, handleSetStepsConfig } = useWorkflowGeneration();
    const stepConfig = getStepConfig(step.id);
    const code = stepConfig?.code || step.config?.code || "";
    const [variables, setVariables] = useState([]);
    const [localValues, setLocalValues] = useState({});
    const debounceRef = useRef(null);
    const codeRef = useRef(code);
    useEffect(() => { codeRef.current = code; }, [code]);

    useEffect(() => {
        const detected = detectUppercaseVariables(code);
        setVariables(detected);
        const vals = {};
        detected.forEach((v) => { vals[v.name] = getDisplayValue(v.value, v.valueType); });
        setLocalValues(vals);
    }, [code]);

    const handleChange = useCallback((varName, newValue) => {
        setLocalValues((prev) => ({ ...prev, [varName]: newValue }));
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const currentCode = codeRef.current;
            const currentVars = detectUppercaseVariables(currentCode);
            const target = currentVars.find((v) => v.name === varName);
            if (!target) return;
            const updatedCode = updateVariableValue(currentCode, target.name, newValue);
            if (updatedCode && updatedCode !== currentCode) {
                handleSetStepsConfig(step.id, { code: updatedCode });
            }
        }, 500);
    }, [step.id, handleSetStepsConfig]);

    if (variables.length === 0) return emptyMessage;

    return (
        <div className="p-4 space-y-3">
            <div className="text-xs text-gray-500 mb-2">
                {variables.length} variable{variables.length !== 1 ? "s" : ""} detected
            </div>
            {variables.map((v) => (
                <VariableField key={v.name} variable={v} value={localValues[v.name] || ""} onChange={handleChange} />
            ))}
        </div>
    );
}

const VariableField = memo(function VariableField({ variable, value, onChange }) {
    const isMultiline = variable.valueType === "template" || (value && value.includes("\n"));
    const handleInputChange = useCallback((e) => onChange(variable.name, e.target.value), [variable.name, onChange]);

    return (
        <div>
            <Label className="text-xs font-mono text-gray-700">{variable.name}</Label>
            {isMultiline ? (
                <textarea
                    className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-lg resize-y min-h-[60px] focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={value}
                    onChange={handleInputChange}
                    rows={3}
                />
            ) : (
                <Input
                    className="mt-1 text-sm"
                    type={variable.valueType === "number" ? "number" : "text"}
                    value={value}
                    onChange={handleInputChange}
                />
            )}
        </div>
    );
});
